import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  normalizeAddress,
  qualifyingInviteCount,
  shortfallMessage,
  splitAddressInput,
} from '../utils/inviteOthers';
import { isValidEmail } from '../utils/email';

// The wizard's step fields are page-scale (matching ProfileFields' inputs); a modal's are not
// (AddSkillsModal / ShareWithColleaguesModal both sit at rounded-md py-2). Same field, two homes.
// min-h holds the box at its chipped height so committing an address doesn't jog the form:
// without it the compact box steps 38px -> 46px when the first chip lands. `comfortable` always
// had one, which is why only the modal jumped.
// Alignment is per-size because the two want opposite things: `comfortable` has 60-odd px of
// slack and stacks from the top, while `compact` is one row tall and would sit its placeholder
// high. `safe` keeps an overfull box scrolling from its first row rather than clipping it.
const BOX_SIZES = {
  comfortable: 'content-start max-h-56 min-h-[6rem] rounded-xl px-3 py-3.5',
  compact: '[align-content:safe_center] max-h-36 min-h-[2.875rem] rounded-md px-3 py-2',
};

// The error state has to claim the focus-within border too: a typo is reported while the caret
// is still in the field, and `focus-within:border-emerald-500` outranks a bare `border-red-500`
// on specificity — it would paint the erroring field green.
const BORDER_STATES = {
  default: 'border-gray-300 focus-within:border-emerald-500 focus-within:ring-emerald-500',
  error: 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500',
};

// The caret's own minimum width, which belongs to the box it sits in: 12rem reads as a field
// at page scale, but in the modal a minimum that wide wrapped the caret onto an empty second
// row as soon as two accepted chips filled the first.
const INPUT_SIZES = {
  comfortable: 'min-w-[12rem]',
  compact: 'min-w-[8rem]',
};

/**
 * InviteOthersForm — the recipient field for both "invite colleagues" asks: the onboarding
 * wizard's invite step and ShareReferralModal. Restyling it changes both.
 *
 * One field rather than a stack of identical inputs: committed addresses become chips
 * and the caret stays put, so the icon and placeholder appear once instead of once per
 * recipient. The "n of threshold" counter carries the ask that the row count used to.
 *
 * `action` is an optional node rendered under the field and its helper text — the field's
 * own submit, in reading order. ShareReferralModal puts its Send CTA there so the button
 * belongs to the field rather than to a modal footer; the wizard omits it and renders
 * identically to before.
 *
 * Committed chips live in `selections.invited`; the uncommitted text lives in
 * `selections.inviteDraft` rather than local state, so a teacher who types an address
 * and clicks Continue without pressing Enter still has it submitted (the wizard folds
 * the draft into the payload).
 *
 * `programKey` names the campaign these invites belong to. The wizard, not this component,
 * makes the POST, so this declares which campaign's copy a given usage of the form asks for
 * and the wizard passes the same key to sendReferralInvites.
 */
export const InviteOthersForm = ({
  selections,
  setSelections,
  threshold,
  programKey,
  isSubmitting = false,
  outcomes = {},
  progress = null,
  submitError = null,
  action = null,
  size = 'comfortable',
  autoFocus = true,
  placeholder = 'Enter emails to share with colleagues (Optional)',
}) => {
  const emails = selections.invited || [];
  const draft = selections.inviteDraft || '';
  const [entryError, setEntryError] = useState(null);
  const inputRef = useRef(null);
  const fieldRef = useRef(null);

  // A modal opens because someone asked for it, so stealing focus there is right; a page
  // card mounts a beat after load, so the same effect would jump focus and scroll the hero
  // mid-read. Both existing (modal) callers leave this at its default.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // A textarea keeps its rows={1} height on its own, so a wrapped second line would be
  // invisible — the same hiding the single-line input did. The caret sits below the chips,
  // so a chip that wraps to a new row can push it past the bottom of the scroll box:
  // pinning the scroll to the bottom keeps whatever is being typed in view. Before paint,
  // since both steps read and write layout.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    const field = fieldRef.current;
    if (field) field.scrollTop = field.scrollHeight;
  }, [draft, emails.length]);

  // Valid addresses become chips; anything that isn't stays in the box next to an error,
  // so pasting a list of five with one typo doesn't reject the other four.
  const commit = (text) => {
    const tokens = splitAddressInput(text);
    if (!tokens.length) return;
    const valid = tokens.filter((token) => isValidEmail(token));
    const invalid = tokens.filter((token) => !isValidEmail(token));
    setSelections((prev) => ({
      ...prev,
      invited: Array.from(new Set([...(prev.invited || []), ...valid])),
      inviteDraft: invalid.join(', '),
    }));
    setEntryError(invalid.length ? 'Please enter a valid email address.' : null);
  };

  const setDraft = (value) => {
    setSelections((prev) => ({ ...prev, inviteDraft: value }));
    setEntryError(null);
  };

  const removeChip = (email) => {
    setSelections((prev) => ({
      ...prev,
      invited: (prev.invited || []).filter((e) => e !== email),
    }));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commit(draft);
      return;
    }
    // Backspace in an empty box pulls the last chip back for editing rather than
    // deleting it outright — a mistyped address is usually worth fixing, not retyping.
    if (e.key === 'Backspace' && !draft && emails.length) {
      e.preventDefault();
      const last = emails[emails.length - 1];
      removeChip(last);
      setDraft(last);
    }
  };

  // Keyed by address rather than by index, so removing a chip naturally clears its status
  // instead of needing invalidation. The map is cumulative across submits (see
  // mergeInviteOutcomes) — the last response alone doesn't describe an earlier batch.
  const outcomeFor = (email) => outcomes[normalizeAddress(email)] || null;

  // Same shape as the class chips in ProfileFields (this wizard's other chip surface):
  // rounded-md, px-2 py-1, emerald for a positive state. Red is reserved for a referral
  // that actually failed; an address the API couldn't use is grey with an x instead.
  const CHIP_TONES = {
    accepted: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-red-100 text-red-800',
    invalid: 'bg-gray-100 text-gray-500',
  };

  // One threshold for both the counter and the nudge, so they can't disagree. The API's
  // is preferred once known; the prop is what's available before the first submit.
  const effectiveThreshold = progress?.threshold ?? threshold;

  // Cumulative across attempts, from the API's progress — a response's `created` is only
  // its own batch, which read "3 of 5" after a 2-then-3 split and never unlocked.
  const shortfall = progress
    ? shortfallMessage({ qualified: progress.qualified_count, threshold: effectiveThreshold })
    : null;

  return (
    <div className='flex flex-col'>
      {effectiveThreshold ? (
        <p className='self-end text-xs font-medium text-gray-500'>
          <span>
            {qualifyingInviteCount({ emails, outcomes, progress })} of {effectiveThreshold}
          </span>
        </p>
      ) : null}

      {/* The whole box is the click target, so tapping the padding focuses the input the
          way a single native field would. Padding and height come from BOX_SIZES, so the empty
          state matches the other fields wherever it is rendered. */}
      <div
        ref={fieldRef}
        onClick={() => inputRef.current?.focus()}
        className={`mt-2 flex flex-wrap items-center gap-2 overflow-y-auto border shadow-sm focus-within:ring-1 ${BORDER_STATES[entryError ? 'error' : 'default']} ${BOX_SIZES[size]}`}>
        {emails.map((email) => {
          const outcome = outcomeFor(email);
          return (
            <span
              key={email}
              className={`flex min-w-0 items-center gap-x-1 rounded-md px-2 py-1 text-sm ${
                CHIP_TONES[outcome] || 'bg-gray-100 text-gray-700'
              }`}>
              {outcome === 'accepted' && <CheckCircleIcon className='h-4 w-4 shrink-0' />}
              {outcome === 'invalid' && <XCircleIcon className='h-4 w-4 shrink-0' />}
              <span className='truncate'>{email}</span>
              <button
                type='button'
                disabled={isSubmitting}
                onClick={() => removeChip(email)}
                aria-label={`Remove ${email}`}
                className='shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50'>
                <XMarkIcon className='h-4 w-4' />
              </button>
            </span>
          );
        })}
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          disabled={isSubmitting}
          inputMode='email'
          autoCapitalize='none'
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => commit(draft)}
          onPaste={(e) => {
            e.preventDefault();
            commit(e.clipboardData.getData('text'));
          }}
          placeholder={emails.length ? '' : placeholder}
          className={`min-h-5 flex-1 resize-none overflow-hidden border-0 p-0 text-sm text-gray-900 focus:outline-none focus:ring-0 disabled:opacity-50 ${INPUT_SIZES[size]}`}
        />
      </div>

      {/* The error replaces the helper rather than stacking under the CTA — it is about the text
          still in the field. Same mt-2 text-xs either way, so a correction doesn't reflow. */}
      {entryError ? (
        <p className='mt-2 text-xs text-red-600'>
          <span>{entryError}</span>
        </p>
      ) : (
        <p className='mt-2 text-xs text-gray-500'>
          <span>Press Enter, comma, or space after each address</span>
        </p>
      )}

      {/* Unwrapped: the caller owns its own spacing, the way any sibling here would. */}
      {action}

      {shortfall && (
        <p className='mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2'>
          <span>{shortfall}</span>
        </p>
      )}
      {submitError && (
        <p className='mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2'>
          <span>{submitError}</span>
        </p>
      )}
    </div>
  );
};

export default InviteOthersForm;
